import type { Reaction } from '../state';
export interface InteractionCapability {
  tool_type: string;
  schema_version: number;
}

export interface ChoiceOption {
  id: string;
  label: string;
  description?: string;
  allow_input?: true;
  input_placeholder?: string;
}

export interface ChoicePresentation {
  label: string;
  description?: string;
  options: ChoiceOption[];
}

export interface CarouselImage {
  url: string;
  alt: string;
}

export interface CarouselItem {
  id: string;
  title: string;
  description?: string;
  image?: CarouselImage;
  action_label?: string;
}

export interface CarouselPresentation {
  label: string;
  description?: string;
  items: CarouselItem[];
}

export interface DatePickerPresentation {
  label: string;
  description?: string;
  selection_mode: 'single' | 'range';
  min_date?: string;
  max_date?: string;
  confirm_label?: string;
}

export type InteractionPresentation =
  | ChoicePresentation
  | CarouselPresentation
  | DatePickerPresentation;

export type InteractionResponse =
  | { kind: 'selection'; value: string }
  | { kind: 'text'; value: string }
  | { kind: 'date'; value: string }
  | { kind: 'date_range'; value: { start_date: string; end_date: string } };

export interface InteractionMessage {
  id: string;
  type: 'interaction';
  role: string;
  content: string;
  created_at?: string;
  interaction_id: string;
  tool_type: string;
  schema_version: number;
  presentation: InteractionPresentation;
  interaction_status: 'preparing' | 'pending' | 'accepted' | 'resumed' | 'cancelled' | 'expired';
  interaction_revision: number;
  selected_option_id?: string | null;
  response_payload?: InteractionResponse | null;
}

export const SUPPORTED_INTERACTIONS = [
  { tool_type: 'ui_buttons', schema_version: 1 },
  { tool_type: 'ui_buttons', schema_version: 2 },
  { tool_type: 'ui_carousel', schema_version: 1 },
  { tool_type: 'ui_date_picker', schema_version: 1 },
] as const;

export interface WireEnvelope {
  type: string;
  session_id?: string;
  account_id?: number | string;
  ts?: string;
  event_id?: string | number;
  payload: Record<string, unknown>;
}

export type WriteState = 'accepted' | 'merged' | 'done' | 'failed' | 'busy' | 'invalid' | 'error';

export interface WriteResult {
  state: WriteState;
  error?: string;
  message_id?: string;
  turn_id?: string;
  interaction_id?: string;
  merged_into?: string;
  retry_after_s?: number;
}

export interface WireMessage {
  id: string;
  role: 'user' | 'ai' | 'assistant' | 'operator' | 'support' | 'system';
  content: string;
  created_at?: string;
  type?: 'message' | 'interaction';
  reactions?: Reaction[];
}

export interface PublicRow extends Partial<Omit<InteractionMessage, 'id' | 'role' | 'content' | 'type' | 'presentation'>> {
  id: string;
  role: 'assistant' | 'user' | 'operator' | 'system';
  content: string;
  ts: string;
  type?: 'message' | 'interaction';
  presentation?: unknown;
  local?: true;
}
